import React from "react";
import { Select, NumberInput, ButtonGroup } from "@caelor/ui";
import { usePeopleStore } from "../../../../../../stores/use-people-store";
import { CORNERS_OPTIONS, getPaginationOptions } from "./defaults";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import { useTranslation } from "react-i18next";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import useCosmosFields from "../../../../../../hooks/use-cosmos-fields";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import MissingAppPanel from "../../../../../../components/ui/missing-app-panel/MissingAppPanel";
import { useStyleguideColors } from "../../../../../../hooks/use-styleguide-colors";
import { DEFAULT_ACCENT_COLOR } from "../../../../../../features/people/defaults";
import { BOOLEAN_OPTIONS } from "../recently-active/defaults";

function LayoutSettings() {
  const { t } = useTranslation();

  const { data, selectedTab, updateConfig, updateConfigTab } = usePeopleStore(
    (state) => ({
      data: state.data,
      selectedTab: state.getSelectedTab(),
      updateConfig: state.updateConfig,
      updateConfigTab: state.updateConfigTab,
    })
  );

  const { fields } = useCosmosFields();

  const { isCosmosInstalled, styleguideSetup } = useOpticsStore((state) => ({
    isCosmosInstalled: state.isCosmosInstalled,
    styleguideSetup: state.styleguideSetup,
  }));

  const styleguideColors = useStyleguideColors({
    styleguideSetup,
  });

  const PaginationOptions = getPaginationOptions(t);

  return (
    <>
      {data.config?.tabs[selectedTab]?.enrichment === "dynamic" && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>{t("display-options")}</SettingsOptionHeading>

          <OptionContainer>
            <OptionLabel>{t("pagination")}</OptionLabel>
            <OptionContent>
              <Select
                options={PaginationOptions}
                value={PaginationOptions.find(
                  ({ value }) => value === data.config.pagination
                )}
                onChange={({ value }) =>
                  updateConfig({ value: { pagination: value } })
                }
              />
            </OptionContent>
          </OptionContainer>

          <OptionContainer>
            <OptionLabel>{t("per-page")}</OptionLabel>
            <OptionContent>
              <NumberInput
                value={data.config.tabs[selectedTab].limit}
                setValue={(value) =>
                  updateConfigTab({ value: { limit: value } })
                }
                min={1}
                isFullWidth
              />
            </OptionContent>
          </OptionContainer>
        </SettingsOptionContainer>
      )}

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("link")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("new-tab")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={data?.config?.openInNewTab ?? true}
              onClick={(isOn) =>
                updateConfig({
                  value: { openInNewTab: isOn },
                })
              }
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>

      {isCosmosInstalled ? (
        <SettingsOptionContainer>
          <OptionContainer direction="column">
            <OptionLabel>{t("subheader")}</OptionLabel>
            <OptionContent full>
              <Select
                value={fields.find(
                  ({ value }) => value === data.config.titleFieldId
                )}
                options={fields}
                onChange={(option) =>
                  updateConfig({
                    value: { titleFieldId: option?.value || "" },
                  })
                }
                placeholder={t("select-subheader")}
                isClearable
              />
            </OptionContent>
          </OptionContainer>

          <OptionContainer direction="column">
            <OptionLabel>{t("label")}</OptionLabel>
            <OptionContent full>
              <Select
                value={fields.find(
                  ({ value }) => value === data.config.footerFieldId
                )}
                options={fields}
                onChange={(option) =>
                  updateConfig({
                    value: { footerFieldId: option?.value || "" },
                  })
                }
                placeholder={t("select-label")}
                isClearable
              />
            </OptionContent>
          </OptionContainer>
        </SettingsOptionContainer>
      ) : (
        <OptionContainer direction="column">
          <OptionLabel>{t("show-team-profiles")}</OptionLabel>
          <MissingAppPanel
            text={t("display-cosmos-information")}
            link={
              "https://marketplace.atlassian.com/apps/1230540/cosmos-intranet-for-confluence-free?hosting=cloud&tab=overview"
            }
            linkText={t("start-trial")}
          />
        </OptionContainer>
      )}

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>

        <OptionContainer>
          <OptionLabel>{t("corners")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={CORNERS_OPTIONS}
              value={data.config.cardBorderRadius}
              options={{ input: true }}
              onClick={(value) =>
                updateConfig({ value: { cardBorderRadius: value } })
              }
              onChange={({ target: { value } }) => {
                if (value.length < 4) {
                  updateConfig({ value: { cardBorderRadius: value } });
                }
              }}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>

        {isCosmosInstalled && (
          <OptionContainer>
            <OptionLabel alignToTop>{t("colors")}</OptionLabel>
            <OptionContent>
              <ColorPicker
                hideColorTiles
                value={{
                  accent:
                    data.config.accentColor ||
                    styleguideColors?.accent ||
                    DEFAULT_ACCENT_COLOR,
                }}
                onChange={({ accent }) => {
                  updateConfig({ value: { accentColor: accent } });
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>
    </>
  );
}

export default LayoutSettings;
